import * as React from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Paper, Typography, Box, Stack } from "@mui/material";
import { ChartDataPoint } from "../ChartDataPoint";

interface Props {
  title?: string;
  data?: ChartDataPoint[];
  headerActions?: React.ReactNode;
  legend?: string;
  children?: React.ReactNode;
}

const LinearChart: React.FC<Props> = ({ title, data, headerActions, legend, children }) => {
  const chartData = React.useMemo(() => {
    if (!data) {
      return [];
    }
    // Check if data spans multiple years
    const years = new Set(data.map(d => new Date(d.timestamp).getFullYear()));
    const hasMultipleYears = years.size > 1;

    return data.map(d => ({
      timestamp: new Date(d.timestamp).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        ...(hasMultipleYears && { year: "numeric" })
      }),
      count: d.count
    }));
  }, [data]);

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={2} alignItems="center">
          {headerActions}
        </Stack>
      </Box>
      {children ? (
        children
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" angle={-45} textAnchor="end" height={80} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="count"
              stroke="#1976d2"
              strokeWidth={2}
              name={legend}
              dot={(data?.length ?? 0) <= 50}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
};

export { LinearChart };
